import { useState } from "react";

const PX = 8, ROW = 46, LABEL_W = 230, PAD = 20, DAY = 86400000;
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export function Gantt({ tasks, wsCode, canEdit, onPatch }) {
  const rows = tasks.filter((t) => t.start_date && t.end_date);
  const [drag, setDrag] = useState(null);
  if (!rows.length) return <div className="text-sm text-[#5A687C]">No scheduled tasks to display.</div>;

  const min = new Date(Math.min(...rows.map((t) => new Date(t.start_date))));
  const max = new Date(Math.max(...rows.map((t) => new Date(t.end_date))));
  const spanDays = Math.max(1, Math.round((max - min) / DAY)) + 4;
  const chartW = spanDays * PX + PAD * 2;
  const off = (iso) => Math.round((new Date(iso) - min) / DAY);
  const barLeft = (t) => PAD + off(t.start_date) * PX + (drag && drag.id === t.id ? drag.dd * PX : 0);
  const barWidth = (t) => Math.max(PX * 2, (off(t.end_date) - off(t.start_date) + 1) * PX);
  const rowY = (i) => i * ROW + ROW / 2;
  const idx = Object.fromEntries(rows.map((t, i) => [t.id, i]));

  const byId = Object.fromEntries(rows.map((t) => [t.id, t]));
  const dur = (t) => (new Date(t.end_date) - new Date(t.start_date)) / DAY + 1;
  const memo = {}, prev = {};
  const longest = (t) => {
    if (memo[t.id] != null) return memo[t.id];
    const dep = t.depends_on_task_id && byId[t.depends_on_task_id];
    if (dep) { memo[t.id] = longest(dep) + dur(t); prev[t.id] = dep.id; } else memo[t.id] = dur(t);
    return memo[t.id];
  };
  rows.forEach(longest);
  let endT = null, best = -1;
  rows.forEach((t) => { if (memo[t.id] > best) { best = memo[t.id]; endT = t; } });
  const critical = new Set();
  let cur = endT;
  while (cur) { critical.add(cur.id); cur = prev[cur.id] ? byId[prev[cur.id]] : null; }

  const onDown = (e, t) => { if (!canEdit) return; e.preventDefault(); setDrag({ id: t.id, x: e.clientX, dd: 0, s: t.start_date, e: t.end_date }); };
  const onMove = (e) => { if (!drag) return; setDrag((d) => ({ ...d, dd: Math.round((e.clientX - d.x) / PX) })); };
  const onUp = () => {
    if (!drag) return;
    if (drag.dd !== 0) onPatch(drag.id, { start_date: addDays(drag.s, drag.dd), end_date: addDays(drag.e, drag.dd) });
    setDrag(null);
  };

  return (
    <div className="overflow-x-auto" onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}>
      <div className="flex text-xs text-[#5A687C] mb-3 gap-4">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-4 rounded bg-[#5A687C]" />Task</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-4 rounded bg-[#BA0C2F]" />Critical path</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-4 rounded bg-[#2E8B57]" />Completed</span>
        {canEdit && <span className="text-[#98A2AF] italic">Drag a bar to reschedule</span>}
      </div>
      <div className="relative" style={{ width: LABEL_W + chartW, height: rows.length * ROW + 10 }}>
        <svg className="absolute pointer-events-none" style={{ left: LABEL_W, top: 0, width: chartW, height: rows.length * ROW }}>
          <defs><marker id="arrow" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#98A2AF" /></marker></defs>
          {rows.map((t) => {
            if (!t.depends_on_task_id || idx[t.depends_on_task_id] == null) return null;
            const d = byId[t.depends_on_task_id];
            const x1 = barLeft(d) + barWidth(d), y1 = rowY(idx[d.id]);
            const x2 = barLeft(t), y2 = rowY(idx[t.id]);
            const crit = critical.has(t.id) && critical.has(d.id);
            return <path key={t.id} d={`M ${x1} ${y1} H ${(x1 + x2) / 2} V ${y2} H ${x2}`}
              stroke={crit ? "#BA0C2F" : "#98A2AF"} strokeWidth={crit ? 2 : 1.5} fill="none" markerEnd="url(#arrow)" />;
          })}
        </svg>
        {rows.map((t, i) => (
          <div key={"lbl" + t.id} className="absolute text-xs text-[#1F2733] truncate pr-3 flex items-center"
            style={{ top: i * ROW, height: ROW, left: 0, width: LABEL_W }}>
            <span><span className="font-bold text-[#5A687C]">{wsCode(t.workstream_id)}</span> · {t.title}</span>
          </div>
        ))}
        {rows.map((t, i) => {
          const crit = critical.has(t.id);
          const bg = t.status === "Blocked" ? "#BA0C2F" : crit ? "#BA0C2F" : t.status === "Completed" ? "#2E8B57" : "#5A687C";
          return (
            <div key={"bar" + t.id} data-testid={`gantt-bar-${t.id}`} onPointerDown={(e) => onDown(e, t)}
              className={`absolute rounded-full flex items-center px-2 text-[10px] font-semibold text-white select-none ${canEdit ? "cursor-grab active:cursor-grabbing" : ""} ${drag && drag.id === t.id ? "ring-2 ring-[#1F2733] z-10" : ""}`}
              style={{ left: LABEL_W + barLeft(t), top: i * ROW + ROW / 2 - 11, height: 22, width: barWidth(t), background: bg }}
              title={`${t.title}: ${t.start_date} → ${t.end_date}`}>
              {t.percent_complete}%
            </div>
          );
        })}
      </div>
    </div>
  );
}
